import React from 'react'
import * as rn from 'react-native'

import * as c from '../../../../common'
import * as r from '../../../../react-utils'

import * as m from '@mui/material'
import * as mi from '@mui/icons-material'
import Entypo from 'react-native-vector-icons/Entypo'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import MaterialIcons from 'react-native-vector-icons/MaterialIcons'
import { useParams } from 'react-router-dom'

import * as cpm from '../../../../app/components'
import * as cp from '../../../comps'
import * as gs from '../../../../app/gStyles'

//@ts-ignore
import styles from './LoanOptions.module.css'

export default React.memo(function LoanOptions() {
  //#region global
  const { customerID } = useParams()
  const totalCost = r.useTotalCost('company', customerID!, true)
  const [solarCompany] = r.useCustomerField(customerID!, 'solarCompany')
  const companyColor = c.companyColors[solarCompany]
  //#endregion global
  //#region local
  const {
    availLoanCompanies,
    availLoanCompaniesNames,
    loanCompany,
    loanCompanyName,
    loanCompanyNameHandle,

    availOffers,
    offer,
    offerName,
    offerNameHandle,

    availTerms,
    term,
    termName,
    termNameHandle: termNameSet,

    availPlans,
    plan,
    planName,
    planNameHandle,
  } = r.usePayment(customerID!)
  const [offerNameOpen, , offerNameToggle] = r.useBool(false)
  /**
   * The longest description so as to render it invisibly and avoid layout
   * shift.
   */
  const longestDesc = React.useMemo(
    (): string =>
      c
        .values(availLoanCompanies)
        .flatMap((lc) => c.values(lc!.offers))
        .map((o) => o.desc)
        .sort((a, b) => b.length - a.length)[0] || '',
    [availLoanCompanies],
  )

  const [longestDescHeight, longestDescHeightSet] = React.useState(0)
  const longestDescLayoutHandle = React.useCallback(
    ({
      nativeEvent: {
        layout: { height },
      },
    }: rn.LayoutChangeEvent) => {
      longestDescHeightSet(height)
    },
    [],
  )
  const offerDescContainerStyle = React.useMemo(
    (): rn.ViewStyle => ({
      ...gs.width72,
      ...gs.deadCenter,
      height: longestDescHeight,
    }),
    [longestDescHeight],
  )

  const [inputtedCost, setInputtedCost, handleInputtedCost] = r.useInputVal(
    totalCost.toString(),
  )
  React.useEffect((): void => {
    if (totalCost) {
      setInputtedCost(totalCost.toString())
    }
  }, [setInputtedCost, totalCost])
  const inputtedCostN = Number(inputtedCost)
  const resetCost = React.useCallback(
    () => void setInputtedCost(totalCost.toString()),
    [setInputtedCost, totalCost],
  )

  const totalLoanAmt = inputtedCostN / (1 - (term?.cost || 0))

  const payPerMonth = React.useMemo((): number => {
    if (!term) return 0
    if (offerName === 'No interest') {
      return totalLoanAmt / term.years / 12
    }

    return c.calcLoanMonthlyPay(totalLoanAmt, term.interest * 100, term.years)
  }, [offerName, term, totalLoanAmt])
  //#endregion local
  //#region styling
  const buttonStyle = React.useMemo(
    () => ({
      backgroundColor: c.companyColors[solarCompany],
      '&:hover': {
        backgroundColor: c.companyColors[solarCompany],
      },
    }),
    [solarCompany],
  )
  //#endregion styling

  const [justCopied, setJustCopied] = React.useState(false)
  const disableJustCopied = React.useCallback(
    () => void setJustCopied(false),
    [],
  )
  const copyTotalLoanAmt = React.useCallback((): void => {
    rn.Clipboard.setString(totalLoanAmt.toFixed(2).toString())
    setJustCopied(true)
  }, [totalLoanAmt])

  return (
    <>
      <div style={sx.inputtedCost}>
        <m.FormControl fullWidth>
          <m.InputLabel htmlFor="outlined-adornment-amount">
            Total Cost
          </m.InputLabel>
          <m.OutlinedInput
            className={styles['total-cost-input']}
            id="outlined-adornment-amount"
            label="Total Cost"
            onChange={handleInputtedCost}
            startAdornment={startAdornment}
            // sx={gs.width100}
            type="number"
            value={inputtedCost}
          />
        </m.FormControl>

        <mi.Replay sx={sx.replayIcon} onClick={resetCost} />
      </div>

      <cpm.Pad amt={50} />

      {/* I would have used a Material UI select but a bug in that library (sigh) mixes up the dropdowns from 2 separate selects for some reason. */}
      <select onChange={loanCompanyNameHandle} value={loanCompanyName}>
        {!loanCompanyName && (
          <option disabled value="">
            Select a company...
          </option>
        )}
        {availLoanCompaniesNames.map((lc) => (
          <option key={lc} value={lc}>
            {lc}
          </option>
        ))}
      </select>

      <cpm.Pad amt={16} />

      <rn.Pressable
        disabled={!loanCompanyName}
        style={loanCompanyName ? gs.rowCentered : gs.rowCenteredOpacity0}
      >
        <MaterialCommunityIcons
          color={offer ? companyColor : c.light.canvas.opaque}
          name={offer?.icon || 'form-dropdown'}
          size={64}
        />
        {gs.colGap8}
        <cpm.Text
          style={{
            alignSelf: 'flex-end',
            color: offerName ? c.light.canvas.color : c.light.canvas.opaque,
            fontFamily: c.light.fontFamily,
            fontSize: 32,
          }}
        >
          {offerName || 'Select an offer...'}
        </cpm.Text>
        {gs.colGap8}
        <Entypo
          color={c.light.canvas.opaque}
          name="select-arrows"
          size={18}
          style={gs.alignSelfEnd}
        />
      </rn.Pressable>

      <m.Select
        onChange={offerNameHandle}
        onClose={offerNameToggle}
        onOpen={offerNameToggle}
        open={offerNameOpen}
        style={sx.select}
        value={offerName}
      >
        {availOffers.map(([availOfferName, availOffer]) => (
          <m.MenuItem
            key={availOfferName}
            style={sx.menuItem}
            value={availOfferName}
          >
            <cpm.View style={gs.rowCentered}>
              <MaterialCommunityIcons
                color={c.light.canvas.opaque}
                name={availOffer.icon}
                size={18}
              />

              {gs.colGap8}
              <cpm.Text colorScheme="light" size={18}>
                {availOfferName}
              </cpm.Text>
            </cpm.View>
            {gs.rowGap8}
            <cpm.View style={gs.rowCentered}>
              <MaterialCommunityIcons
                color={c.light.canvas.opaque}
                name="abjad-arabic"
                size={18}
                // TODO: ??
                style={gs.opacity0}
              />
              {gs.colGap8}
              <cpm.Text colorScheme="light" size={16}>
                {availOffer.shortDesc}
              </cpm.Text>
            </cpm.View>
          </m.MenuItem>
        ))}
      </m.Select>

      {gs.rowGap24}

      <cpm.View style={offerName ? gs.rowCentered : gs.rowCenteredOpacity0}>
        <cpm.View
          backgroundColor={c.light.canvas.opaque}
          height={1}
          width={120}
        />
        {gs.colGap8}
        <cpm.Text
          style={{
            color: c.light.canvas.color,
            fontFamily: c.light.canvas.fontFamily,
            fontSize: 14,
          }}
        >
          How it works
        </cpm.Text>
        {gs.colGap8}
        <cpm.View
          backgroundColor={c.light.canvas.opaque}
          height={1}
          width={120}
        />
      </cpm.View>

      {gs.rowGap12}

      <cpm.View style={offerDescContainerStyle}>
        {longestDescHeight === 0 && (
          <rn.Text onLayout={longestDescLayoutHandle} style={sx.longestDesc}>
            {longestDesc}
          </rn.Text>
        )}

        <cpm.Text centerText colorScheme="light" size={16}>
          {offer?.desc}
        </cpm.Text>
      </cpm.View>

      {gs.rowGap24}

      <cpm.View
        backgroundColor={offerName ? c.light.canvas.opaque : '#FFFFFF'}
        height={1}
        width={120}
      />

      {gs.rowGap24}

      <cpm.View style={offer?.bullets ? gs.opacity1 : gs.opacity0}>
        {(offer?.bullets || emptyBullets).map((bullet, i) => (
          <React.Fragment key={bullet.text}>
            {i > 0 && gs.rowGap12}
            <cpm.View style={gs.rowCentered}>
              <MaterialCommunityIcons
                color={companyColor}
                name={bullet.icon}
                size={26}
              />
              {gs.colGap8}
              <cpm.Text colorScheme="light" on="canvas" size={14}>
                {bullet.text}
              </cpm.Text>
            </cpm.View>
          </React.Fragment>
        ))}

        {offer?.bullets.length === 2 && (
          // Always have the bullet list take up the same height
          <>
            {gs.rowGap12}

            <cpm.View style={gs.rowCenteredOpacity0}>
              <MaterialCommunityIcons
                color={companyColor}
                name="bank"
                size={26}
              />
              {gs.colGap8}
              <cpm.Text colorScheme="light" on="canvas" size={14}>
                {'[[SHOULD NOT BE VISIBLE]]'}
              </cpm.Text>
            </cpm.View>
          </>
        )}
      </cpm.View>

      {gs.rowGap24}

      {
        // This true and the fragment reduces diff
        true && (
          <>
            <cpm.View
              style={
                availPlans.length > 0 ? gs.rowCentered : gs.rowCenteredOpacity0
              }
            >
              <cpm.View
                backgroundColor={c.light.canvas.opaque}
                height={1}
                width={120}
              />
              {gs.colGap8}
              <cpm.Text
                style={{
                  color: c.light.canvas.color,
                  fontFamily: c.light.canvas.fontFamily,
                  fontSize: 14,
                }}
              >
                Choose plan
              </cpm.Text>
              {gs.colGap8}
              <cpm.View
                backgroundColor={c.light.canvas.opaque}
                height={1}
                width={120}
              />
            </cpm.View>

            {gs.rowGap24}

            <cpm.View style={gs.rowCentered}>
              {availPlans.map(([availPlanName]) => (
                <rn.Pressable
                  key={availPlanName}
                  onPress={() => {
                    planNameHandle(availPlanName)
                  }}
                  style={{
                    backgroundColor: companyColor,
                    borderRadius: 8,
                    marginHorizontal: 12,
                    padding: 12,
                  }}
                >
                  <cpm.Text bold centerText colorScheme="dark">
                    {availPlanName}
                  </cpm.Text>

                  {planName === availPlanName && (
                    <cpm.View
                      style={{
                        ...gs.absolute,
                        ...gs.deadCenter,
                        backgroundColor: '#FFF',
                        borderTopLeftRadius: 4,
                        bottom: 0,
                        padding: 1,
                        right: 0,
                      }}
                    >
                      <MaterialCommunityIcons
                        color={companyColor}
                        name="check"
                        size={12}
                      />
                    </cpm.View>
                  )}
                </rn.Pressable>
              ))}
            </cpm.View>
          </>
        )
      }

      {gs.rowGap24}

      <cpm.View
        style={availTerms.length > 0 ? gs.rowCentered : gs.rowCenteredOpacity0}
      >
        <cpm.View
          backgroundColor={c.light.canvas.opaque}
          height={1}
          width={120}
        />
        {gs.colGap8}
        <cpm.Text
          style={{
            color: c.light.canvas.color,
            fontFamily: c.light.canvas.fontFamily,
            fontSize: 14,
          }}
        >
          Choose terms
        </cpm.Text>
        {gs.colGap8}
        <cpm.View
          backgroundColor={c.light.canvas.opaque}
          height={1}
          width={120}
        />
      </cpm.View>

      {gs.rowGap12}

      <cpm.View style={availTerms.length > 0 ? sx.terms : gs.opacity0}>
        {availTerms.map(([availTermName, availTerm]) => (
          <rn.Pressable
            key={availTermName}
            onPress={() => {
              termNameSet(availTermName)
            }}
            style={{
              backgroundColor: companyColor,
              borderRadius: 8,
              marginHorizontal: 12,
              padding: 12,
            }}
          >
            <cpm.Text bold centerText colorScheme="dark">
              {availTerm.years}yr / {(availTerm.interest * 100).toFixed(2)}%
            </cpm.Text>

            {termName === availTermName && (
              <cpm.View
                style={{
                  ...gs.absolute,
                  ...gs.deadCenter,
                  backgroundColor: '#FFF',
                  borderTopLeftRadius: 4,
                  bottom: 0,
                  padding: 1,
                  right: 0,
                }}
              >
                <MaterialCommunityIcons
                  color={companyColor}
                  name="check"
                  size={12}
                />
              </cpm.View>
            )}
          </rn.Pressable>
        ))}
      </cpm.View>

      {gs.rowGap32}

      {/* The true here is to reduce diff */}
      {!!true && (
        <>
          <cpm.View>
            <cpm.View style={term ? gs.rowCentered : gs.rowCenteredOpacity0}>
              <cpm.View
                backgroundColor={c.light.canvas.opaque}
                height={1}
                width={120}
              />
              {gs.colGap8}
              <cpm.Text
                style={{
                  color: c.light.canvas.color,
                  fontFamily: c.light.canvas.fontFamily,
                  fontSize: 14,
                }}
              >
                Selected loan
              </cpm.Text>
              {gs.colGap8}
              <cpm.View
                backgroundColor={c.light.canvas.opaque}
                height={1}
                width={120}
              />
            </cpm.View>

            {gs.rowGap12}

            <cpm.Text centerText colorScheme="light" size={18}>
              {term?.desc}
            </cpm.Text>
          </cpm.View>

          <cpm.View
            alignItems="flex-end"
            style={!term && gs.opacity0}
            width="90%"
          >
            <rn.View style={gs.rowCentered}>
              <MaterialIcons
                color={companyColor}
                name="content-copy"
                onPress={copyTotalLoanAmt}
                size={14}
              />
              {gs.colGap8}
              <cpm.Text colorScheme="light" size={10}>
                Total loan amount: ${totalLoanAmt.toFixed(2)}
              </cpm.Text>
            </rn.View>
            <cpm.Text colorScheme="light" size={10}>
              Dealer fee: {((term?.cost || 0) * 100).toFixed(2)}%
            </cpm.Text>
            <cpm.Text colorScheme="light" size={10}>
              APR: {((term?.interest || 0) * 100).toFixed(2)}%
            </cpm.Text>
            {term?.loanCode && (
              <cpm.Text colorScheme="light" size={10}>
                Loan code: {term?.loanCode}
              </cpm.Text>
            )}
            {term?.minAmt && (
              <cpm.Text colorScheme="light" size={10}>
                Min amt:
                {Intl.NumberFormat('en-US', {
                  currency: 'USD',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                  style: 'currency',
                }).format(term?.minAmt)}
              </cpm.Text>
            )}
            {term?.maxAmt && (
              <cpm.Text colorScheme="light" size={10}>
                Max amt:
                {Intl.NumberFormat('en-US', {
                  currency: 'USD',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                  style: 'currency',
                }).format(term?.maxAmt)}
              </cpm.Text>
            )}
            {loanCompany?.contractorID && (
              <cpm.Text colorScheme="light" size={10}>
                Contractor/Dealer #: {loanCompany?.contractorID}
              </cpm.Text>
            )}
            {loanCompany?.programPhoneNumber && (
              <cpm.Text colorScheme="light" size={10}>
                Program phone number: {loanCompany?.programPhoneNumber}
              </cpm.Text>
            )}
          </cpm.View>
        </>
      )}

      {gs.rowGap24}

      <cpm.View
        backgroundColor={c.light.canvas.separator}
        height={2}
        width="100%"
      />

      <cpm.View
        justifyContent="center"
        flexDirection="row"
        flexGrow={1}
        width="100%"
      >
        <cpm.View
          alignItems="center"
          borderRightColor={c.light.canvas.separator}
          borderRightWidth={1}
          paddingHorizontal={12}
          paddingTop={12}
          width="50%"
        >
          <cpm.Text style={sx.title}>Cash</cpm.Text>

          {gs.rowGap16}

          <MaterialCommunityIcons color={companyColor} name="cash" size={56} />

          {gs.rowGap16}

          <cpm.Text colorScheme="light" size={24}>
            ${inputtedCostN.toFixed(2)}
          </cpm.Text>
        </cpm.View>

        <cpm.View
          alignItems="center"
          borderLeftColor={c.light.canvas.separator}
          borderLeftWidth={1}
          paddingHorizontal={12}
          paddingTop={12}
          width="50%"
        >
          <cpm.Text style={sx.title}>Financing</cpm.Text>

          {gs.rowGap16}

          <MaterialCommunityIcons color={companyColor} name="bank" size={56} />

          {gs.rowGap32}

          <cpm.Text colorScheme="light" centerText size={24}>
            ${payPerMonth.toFixed(0)} / mo
          </cpm.Text>
          <cpm.Text centerText colorScheme="light" size={8}>
            (Estimate)
          </cpm.Text>

          {offer?.choicesInMonths && (
            <>
              {gs.rowGap32}

              <cpm.View
                backgroundColor={c.light.canvas.separator}
                height={2}
                width="100%"
              />

              {gs.rowGap16}

              <cpm.Text style={sx.title}>
                The first {plan?.months} months
              </cpm.Text>

              {gs.rowGap16}

              <MaterialCommunityIcons
                color={companyColor}
                name="calendar"
                size={56}
              />

              {gs.rowGap16}

              <cpm.Text colorScheme="light">You can choose either:</cpm.Text>

              {gs.rowGap16}

              <cpm.Text colorScheme="light">
                {offer?.choicesInMonths[0]}
              </cpm.Text>

              <cpm.View style={gs.rowCentered}>
                <cpm.View
                  backgroundColor={c.light.canvas.separator}
                  height={2}
                  width={64}
                />
                <cpm.Text colorScheme="light">Or</cpm.Text>
                <cpm.View
                  backgroundColor={c.light.canvas.separator}
                  height={2}
                  width={64}
                />
              </cpm.View>

              <cpm.Text colorScheme="light">
                {offer?.choicesInMonths[1]}
              </cpm.Text>
            </>
          )}
        </cpm.View>
      </cpm.View>

      {gs.rowGap24}

      <m.Button
        onClick={handleOpenQualifyTool}
        sx={buttonStyle}
        variant="contained"
      >
        Qualify Tool
      </m.Button>

      <cp.ToastAlert
        duration={2000}
        handleClose={disableJustCopied}
        message="Copied to Clipboard"
        open={justCopied}
        severity="info"
        type="alert"
      />
    </>
  )
})

const lenSx = {
  borderRadius: '10px',
  paddingLeft: '10px',
  paddingRight: '10px',
  color: '#fff',
}

const sx = {
  interest: { width: '100px' },
  inputtedCost: { ...gs.rowSpaceBetween100, padding: '0 24px' },
  len: lenSx,
  lenSelected: { ...lenSx, backgroundColor: '#6C757D' },
  listSubheader: {
    ...gs.userSelectNone,
    alignItems: 'center',
  },
  longestDesc: {
    fontFamily: c.light.fontFamily,
    fontSize: 16,
    opacity: 0,
    textAlign: 'center',
    textAlignVertical: 'center',
    ...gs.userSelectNone,
  } as const,
  menuItem: {
    alignItems: 'stretch',
    flexDirection: 'column',
    paddingBottom: 24,
    paddingTop: 24,
  } as const,
  replayIcon: { alignSelf: 'center', fontSize: '29px', cursor: 'pointer' },
  select: { ...gs.width72, height: 0, opacity: 0 },
  selectCompany: { ...gs.width24, height: 0, opacity: 0 },
  terms: {
    ...gs.rowWrap,
    //@ts-ignore
    gap: 16,
    justifyContent: 'center' as const,
  },
  title: {
    fontFamily: c.light.fontFamily,
    fontSize: 36,
    textAlign: 'center',
  } as const,
}

const startAdornment = <m.InputAdornment position="start">$</m.InputAdornment>

const handleOpenQualifyTool = () => {
  window.open('https://ecohome.pre-fi.com/pre-qualification', '_blank')?.focus()
}

const emptyBullets: r.Bullets = [
  {
    icon: 'bank',
    text: '[[SHOULD NOT BE VISIBLE1]]',
  },
  {
    icon: 'bank',
    text: '[[SHOULD NOT BE VISIBLE2]]',
  },
  {
    icon: 'bank',
    text: '[[SHOULD NOT BE VISIBLE3]]',
  },
]
